import { Link } from "react-router-dom";
import { History, X } from "lucide-react";
import { useI18n } from "../../i18n";
import { useCart } from "../../context/CartContext";
import { fmtDate, fmtMoney } from "../../lib/format";
import { canPieces, unitPrice } from "../../lib/uom";
import { offerFor, useOffers } from "../../lib/offers";
import { trackPromo } from "./promoTrack";

const ModeToggle = ({ code, mode, setMode }) => {
  const { t } = useI18n();
  return (
    <div className="grid grid-cols-2 rounded-full bg-slate-100 p-0.5 text-[11px] font-semibold" data-testid={`row-mode-${code}`}>
      {["box", "piece"].map((m) => (
        <button key={m} type="button" onClick={() => setMode(code, m)} aria-pressed={mode === m} data-testid={`row-mode-${code}-${m}`}
          className={`h-7 rounded-full px-2 ${mode === m ? "bg-navy text-white" : "text-slate-600"}`}>{t(m === "box" ? "x.uom.box" : "x.uom.piece")}</button>
      ))}
    </div>
  );
};

// Dense row for fast ordering: type the quantity, Tab/Enter to the next product
export const ProductRow = ({ item, price, last, promo }) => {
  const { t, lang } = useI18n();
  const { qtyOf, setQty, modeOf, setMode } = useCart();
  const qty = qtyOf(item.code);
  const mode = modeOf(item.code, item);
  const offers = useOffers();
  const normalUnit = unitPrice(item, price, mode);
  const offer = offerFor(offers, item.code, mode, normalUnit, Math.max(qty, 1));
  const unit = offer ? offer.offer_price : normalUnit;
  const onQty = (e) => {
    const q = Math.max(0, Math.min(9999, parseInt(e.target.value || "0", 10) || 0));
    if (promo && !qty && q > 0) trackPromo(promo.id, "add_to_cart", item.code);
    setQty(item.code, q, item);
  };
  const onKey = (e) => {
    if (e.key !== "Enter") return;
    const all = [...document.querySelectorAll("[data-row-qty]")];
    all[all.indexOf(e.target) + 1]?.focus();
  };
  return (
    <li data-testid={`product-row-${item.code}`} className={`flex items-center gap-2 px-2 py-2 sm:gap-3 sm:px-3 ${qty ? "bg-brand/10" : ""}`}>
      <Link to={`/app/products/${encodeURIComponent(item.code)}`} className="min-w-0 flex-1" data-testid={`row-link-${item.code}`}>
        <p className="line-clamp-2 text-[13px] font-semibold leading-tight text-navy sm:truncate sm:text-sm">{item[`name_${lang}`]}</p>
        <p className="truncate text-[11px] text-slate-500">
          {item.code}{item.units_per_box > 0 && ` · ${item.units_per_box}/${t("x.uom.box")}`}
          {last && <span className="ml-1 inline-flex items-center gap-0.5" data-testid={`row-last-${item.code}`}><History className="h-3 w-3" />{fmtDate(last.date)} · {last.qty_boxes}</span>}
        </p>
        <p className="text-xs font-bold tabular-nums text-navy sm:hidden">{fmtMoney(unit)} <span className="font-normal text-slate-500">/ {t(mode === "piece" ? "x.uom.piece" : "x.uom.box")}</span></p>
      </Link>
      <p className="hidden w-28 shrink-0 text-right text-sm font-bold tabular-nums text-navy sm:block" data-testid={`row-price-${item.code}`}>
        {fmtMoney(unit)}<span className="block text-[10px] font-normal text-slate-500">/ {t(mode === "piece" ? "x.uom.piece" : "x.uom.box")}</span>
      </p>
      <div className="w-[76px] shrink-0 sm:w-[88px]">{canPieces(item) && <ModeToggle code={item.code} mode={mode} setMode={setMode} />}</div>
      <input type="number" inputMode="numeric" min={0} value={qty || ""} placeholder="0" onChange={onQty} onKeyDown={onKey} onFocus={(e) => e.target.select()}
        data-row-qty aria-label={item[`name_${lang}`]} data-testid={`row-qty-${item.code}`}
        className="h-10 w-12 shrink-0 rounded-lg border border-slate-200 bg-white text-center text-base font-bold tabular-nums text-navy outline-none focus:border-navy focus:ring-2 focus:ring-brand/40 sm:w-14 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none" />
      <p className="hidden w-24 shrink-0 text-right text-sm tabular-nums text-slate-600 md:block" data-testid={`row-total-${item.code}`}>{qty ? fmtMoney(qty * unit) : "—"}</p>
      <button type="button" disabled={!qty} onClick={() => setQty(item.code, 0, item)} aria-label={t("shop.removeFromCart")} data-testid={`row-remove-${item.code}`}
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${qty ? "text-rose-600 hover:bg-rose-50" : "invisible"}`}><X className="h-4 w-4" /></button>
    </li>
  );
};
